import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Spacer,
  Button,
} from "@chakra-ui/react";
import DataTable from "../AppDataTable";
import { tableCustomStyles } from "../../themes/TableStyles";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faSave, faTable } from "@fortawesome/free-solid-svg-icons";
import { Search, Clear } from "../../themes/IconButtonSet";
import { StandardPageTitle, StandardHeader } from "../../themes/StandardHeader";

const VaccineReservationConfirmation = () => {
  const { sessionId } = useAuth();
  const [filterData, setFilterData] = useState({
    DeliveryPeriod: "",
    PickupPoint: "",
    VaccineID: "",
  });
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [vaccineOptions, setVaccineOptions] = useState([]);
  const [reservationData, setReservationData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [deliveryPeriods, setDeliveryPeriods] = useState([]);

  // Fetch Delivery Periods
  const fetchDeliveryPeriods = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getDeliveryPeriodList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setDeliveryPeriods(response.DeliveryPeriods || []);
      } else {
        console.error("Failed to fetch delivery periods:", response.error);
      }
    } catch (error) {
      console.error("Error fetching delivery periods:", error);
    }
  };

  // Fetch Warehouse Options
  const fetchWarehouseOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinePickupPointList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setWarehouseOptions(response.VaccineWarehousesList || []);
      } else {
        console.error("Failed to fetch warehouses:", response.error);
      }
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    }
  };

  // Fetch Vaccine Options
  const fetchVaccineOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setVaccineOptions(response.VaccinesList || []);
      } else {
        console.error("Failed to fetch vaccines:", response.error);
      }
    } catch (error) {
      console.error("Error fetching vaccines:", error);
    }
  };

  // Fetch Reservation Summary Report
  const fetchReservationReport = async () => {
    /*
    if (!filterData.DeliveryPeriod) {
      alert("กรุณาเลือก รอบการจัดส่ง");
      return;
    }
      */
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineReservationSummaryReport",
        data: {
          DeliveryPeriod: filterData.DeliveryPeriod,
          PickupPoint: filterData.PickupPoint || "",
          VaccineID: filterData.VaccineID || "",
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        // Initialize approved quantities with requested quantities
        const initializedData = response.summary.map((item) => ({
          ...item,
          ApprovedQuantity: item.TotalQuantity,
          ApprovedDoses: item.TotalQuantity * item.PackageSize,
        }));
        setReservationData(initializedData);
      } else {
        setReservationData([]);
        alert("ไม่พบข้อมูล: " + response.error);
      }
    } catch (error) {
      console.error("Error fetching reservation report:", error);
      alert("เกิดข้อผิดพลาดในการดึงข้อมูล");
    } finally {
      setIsLoading(false);
    }
  };

  // Save Confirmation Data
  const saveConfirmation = async () => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/saveVaccineReservationConfirmation",
        data: {
          Confirmations: reservationData.map((item) => ({
            DeliveryPeriod: item.DeliveryPeriod, //filterData.DeliveryPeriod,
            PickupPointID: item.PickupPointID,
            VaccineID: item.VaccineID,
            ApprovedQuantity: item.ApprovedQuantity,
            ApprovedDoses: item.ApprovedDoses,
            UnitPrice: item.UnitPrice,
            TotalPrice: item.TotalPrice,
          })),
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        alert("บันทึกข้อมูลสำเร็จ");
      } else {
        alert("เกิดข้อผิดพลาดในการบันทึก: " + response.error);
      }
    } catch (error) {
      console.error("Error saving confirmation:", error);
      alert("เกิดข้อผิดพลาดในการบันทึกข้อมูล");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchDeliveryPeriods();
      fetchWarehouseOptions();
      fetchVaccineOptions();
    }
  }, [sessionId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilterData({ ...filterData, [name]: value });
  };

  const handleApprovedQuantityChange = (index, value) => {
    const newData = [...reservationData];
    const approvedQty = parseInt(value) || 0;
    newData[index].ApprovedQuantity = approvedQty;
    newData[index].ApprovedDoses = approvedQty * newData[index].PackageSize;
    setReservationData(newData);
  };

  const columns = [
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>รอบการจัดส่ง</span>
        </HStack>
      ),
      selector: (row) => row.DeliveryPeriod,
      sortable: true,
      width: "250px"
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จุดรับวัคซีน</span>
        </HStack>
      ),
      selector: (row) => row.PickupPointName,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>วัคซีน</span>
        </HStack>
      ),
      selector: (row) => row.VaccineNameTH,
      sortable: true,
      width: "250px"
    },
    /*
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ขนาดบรรจุ</span>
        </HStack>
      ),
      selector: (row) => row.PackageSize,
      sortable: true,
    },
    */
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จำนวนที่สั่งจอง (ขวด)</span>
        </HStack>
      ),
      selector: (row) => row.TotalQuantity?.toLocaleString(),
      sortable: true,
      width: "150px"
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จำนวนที่สั่งจอง (โด๊ส)</span>
        </HStack>
      ),
      selector: (row) => row.TotalDoses?.toLocaleString(),
      sortable: true,
      width: "150px"
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จำนวนที่อนุมัติ (ขวด)</span>
        </HStack>
      ),
      cell: (row, index) => (
        <Input
          type="number"
          value={row.ApprovedQuantity}
          onChange={(e) => handleApprovedQuantityChange(index, e.target.value)}
          min={0}
          width="100px"
        />
      ),
      sortable: true,
      width: "150px"
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จำนวนที่อนุมัติ (โด๊ส)</span>
        </HStack>
      ),
      selector: (row) => row.ApprovedDoses?.toLocaleString(),
      sortable: true,
      width: "150px"
    },
    /*
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ราคาต่อหน่วย</span>
        </HStack>
      ),
      selector: (row) => row.UnitPrice?.toLocaleString(),
      sortable: true,
    },
    */
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>มูลค่ารวม</span>
        </HStack>
      ),
      selector: (row) => row.TotalPrice?.toLocaleString(),
      sortable: true,
      width: "150px"
    },
  ];

  return (
    <Box p={35} m={30} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <StandardPageTitle title="การยืนยันการสั่งจองวัคซีน" />

      {/* Filter Form */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mt={5} mb={6}>
        <StandardHeader
          title="ค้นหาข้อมูลการจอง"
          bgColor="iBlue.600"
          icon={faSearch}
        />
        <VStack spacing={6} p={5}>
          <HStack spacing={6} w="100%" flexWrap="wrap">
            <FormControl minW="200px" flex="1" isRequired>
              <FormLabel fontSize="xl" color="gray">
                รอบการจัดส่ง
              </FormLabel>
              <Select
                name="DeliveryPeriod"
                value={filterData.DeliveryPeriod}
                onChange={handleInputChange}
                placeholder="เลือกรอบการจัดส่ง"
                colorScheme="iBlue"
              >
                {deliveryPeriods.map((period) => (
                  <option key={period} value={period}>
                    {period}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                จุดรับวัคซีน
              </FormLabel>
              <Select
                name="PickupPoint"
                value={filterData.PickupPoint}
                onChange={handleInputChange}
                placeholder="เลือกจุดรับวัคซีน (ทั้งหมด)"
                colorScheme="iBlue"
              >
                {warehouseOptions.map((warehouse) => (
                  <option
                    key={warehouse.WarehouseID}
                    value={warehouse.WarehouseID}
                  >
                    {warehouse.WarehouseName}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                วัคซีน
              </FormLabel>
              <Select
                name="VaccineID"
                value={filterData.VaccineID}
                onChange={handleInputChange}
                placeholder="เลือกวัคซีน (ทั้งหมด)"
                colorScheme="iBlue"
              >
                {vaccineOptions.map((vaccine) => (
                  <option key={vaccine.VaccineID} value={vaccine.VaccineID}>
                    {vaccine.VaccineNameTH}
                  </option>
                ))}
              </Select>
            </FormControl>
          </HStack>
          <Box w="100%" textAlign="right">
            <HStack spacing={5} justifyContent="flex-end">
              <Clear
                onClick={() =>
                  setFilterData({
                    DeliveryPeriod: "",
                    PickupPoint: "",
                    VaccineID: "",
                  })
                }
              />
              <Search onClick={fetchReservationReport} isLoading={isLoading} />
            </HStack>
          </Box>
        </VStack>
      </Box>

      {/* Data Table */}
      {reservationData.length > 0 && (
        <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
          <StandardHeader
            title="รายการยืนยันการสั่งจองวัคซีน"
            bgColor="iOrange.500"
            icon={faTable}
          />
          <HStack spacing={5} m={5}>
            <Spacer />
            <Button
              leftIcon={<FontAwesomeIcon icon={faSave} />}
              colorScheme="iBlue"
              onClick={saveConfirmation}
              isLoading={isLoading}
            >
              บันทึก
            </Button>
          </HStack>
          <Box p={5} overflowX="auto">
            <DataTable
              columns={columns}
              data={reservationData}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default VaccineReservationConfirmation;